import { Injectable } from '@angular/core';

import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessageType, ProblemDetails } from '../../../../core/models';
import * as fromRoot from '../../../../core/store';

import * as fromActions from '../actions';
import * as fromModels from '../../models';
import * as fromServices from '../../services';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class SqlQueryEffects {
  constructor(private actions$: Actions, private opusJobsService: fromServices.SqlQueryService) {}

  loadDatabaseSchemas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SqlQueryActionTypes.LoadDatabaseSchemas),
      switchMap((action: fromActions.LoadDatabaseSchemas) => {
        return this.opusJobsService.getTableSchamas(action.payload).pipe(
          map((results: fromModels.TableSchema[]) => {
            return new fromActions.LoadDatabaseSchemasSuccess(results);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.LoadDatabaseSchemasFailure(res)))
        );
      })
    )
  );

  loadDatabaseSchemasFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SqlQueryActionTypes.LoadDatabaseSchemasFailure),
      switchMap((action: fromActions.LoadDatabaseSchemasFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  runSqlQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SqlQueryActionTypes.RunSqlQuery),
      switchMap((action: fromActions.RunSqlQuery) => {
        return this.opusJobsService.runSqlQuery(action.payload).pipe(
          map((results: fromModels.QueryResponse) => {
            return new fromActions.RunSqlQuerySuccess(results);
          }),
          catchError((res: HttpErrorResponse) => of(new fromActions.RunSqlQueryFailure(res)))
        );
      })
    )
  );

  runSqlQueryFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SqlQueryActionTypes.RunSqlQueryFailure),
      switchMap((action: fromActions.RunSqlQueryFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  private getErrorMessage(response: HttpErrorResponse | string): string {
    if (typeof response === 'string') return response;

    if (response.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    if (response.status === 400 && response.error) {
      try {
        if (typeof response.error === 'string') return response.error;

        const problemDetails = response.error as ProblemDetails;
        if (problemDetails) {
          const firstError = problemDetails.errors ? problemDetails.errors[Object.keys(problemDetails.errors)[0]] : null;
          if (firstError) return firstError;
          else if (problemDetails.title) return problemDetails.title;
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    return response.error ? response.error.message : response.message;
  }
}
